import { baseApiUrl, fetcher, handleError, ServerErrorResponse } from 'data_sources/constants';

import type { CreateRelatedOrganizationRequest } from '../_types';

/**
 * Creates a risk pool relationship between organizations
 */
export const createRiskPoolRelation = async (
  organizationId: string,
  data: CreateRelatedOrganizationRequest,
  onError?: (errors: ServerErrorResponse) => void
): Promise<any> => {
  const url = `${baseApiUrl()}/lexid/related-orgs/v1/related-organizations/${organizationId}`;
  try {
    const response = await fetcher({
      url,
      method: 'post',
      body: data,
    });
    return response;
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null) {
      handleError(error as Error, { onError });
    }
    throw error;
  }
};

/**
 * Deletes a risk pool relationship between organizations
 */
export const deleteRiskPoolRelation = async (
  organizationId: string,
  childOrgId: string,
  onError?: (errors: ServerErrorResponse) => void
): Promise<any> => {
  const url = `${baseApiUrl()}/lexid/related-orgs/v1/related-organizations/${organizationId}/child/${childOrgId}`;
  try {
    const response = await fetcher({
      url,
      method: 'delete',
    });
    return response;
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null) {
      handleError(error as Error, { onError });
    }
    throw error;
  }
};

/**
 * Hook that provides functions to manage risk pool organization relationships
 */
export default function useManageRiskPoolOrganizations() {
  const create = async (
    organizationId: string,
    data: CreateRelatedOrganizationRequest,
    callback?: (data: any) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    try {
      const response = await createRiskPoolRelation(organizationId, data, onError);
      if (callback) callback(response);
    } catch (error) {
      // Error already handled in createRiskPoolRelation
    }
  };

  const destroy = async (
    organizationId: string,
    childOrgId: string,
    callback?: (data: any) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    try {
      const response = await deleteRiskPoolRelation(organizationId, childOrgId, onError);
      if (callback) callback(response);
    } catch (error) {
      // Error already handled in deleteRiskPoolRelation
    }
  };

  return {
    create,
    destroy,
  };
}
